import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Package, ShoppingBag, Users, DollarSign, AlertTriangle, ArrowUpRight } from 'lucide-react';
import AdminLayout from '../../components/admin/AdminLayout';
import { adminAPI } from '../../utils/api';
import { useSettings } from '../../context/SettingsContext';

function StatCard({ icon: Icon, label, value, color, trend, sub }) {
  return (
    <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ width: 48, height: 48, background: `${color}18`, borderRadius: 14, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon size={22} color={color} />
        </div>
        {trend != null && (
          <span style={{ fontSize: 12, fontWeight: 700, color: trend >= 0 ? 'var(--success)' : 'var(--danger)', background: trend >= 0 ? 'rgba(34,197,94,0.1)' : 'rgba(244,63,94,0.1)', padding: '4px 10px', borderRadius: 8, display: 'flex', alignItems: 'center', gap: 4 }}>
            <ArrowUpRight size={12} style={{ transform: trend < 0 ? 'rotate(180deg)' : 'none' }} />
            {Math.abs(trend)}%
          </span>
        )}
      </div>
      <div>
        <div style={{ color: 'var(--text-muted)', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>{label}</div>
        <div style={{ fontFamily: 'var(--font-display)', fontSize: 32, fontWeight: 700, letterSpacing: '-0.04em' }}>{value}</div>
        {sub && <div style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 4 }}>{sub}</div>}
      </div>
    </div>
  );
}

function statusColor(s) {
  const m = { pending: '#f59e0b', confirmed: '#22c55e', processing: '#7c6dfa', shipped: '#38bdf8', delivered: '#22c55e', cancelled: '#f43f5e' };
  return m[s] || '#f59e0b';
}

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { t } = useSettings();

  useEffect(() => {
    adminAPI.getStats().then(res => setData(res.data)).catch(console.error).finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <AdminLayout title={t('admin', 'dashboard')}>
      <div className="flex-center" style={{ height: 400 }}><div className="spinner" /></div>
    </AdminLayout>
  );

  const { stats, recentOrders, lowStock } = data;

  return (
    <AdminLayout title={t('admin', 'dashboard')}>

      {/* ── Stat cards ── */}
      <div className="admin-stats-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px,1fr))', gap: 16, marginBottom: 24 }}>
        <StatCard icon={DollarSign} label={t('admin', 'revenue')} value={`$${stats.totalRevenue.toLocaleString(undefined, { maximumFractionDigits: 0 })}`} color="#e8a020" trend={12} sub="from paid orders" />
        <StatCard icon={ShoppingBag} label={t('admin', 'total_orders')} value={stats.totalOrders} color="#7c6dfa" sub={`${stats.pendingOrders} pending`} trend={8} />
        <StatCard icon={Package} label={t('admin', 'total_products')} value={stats.totalProducts} color="#22c55e" sub="active listings" />
        <StatCard icon={Users} label={t('admin', 'total_users')} value={stats.totalUsers} color="#38bdf8" trend={5} sub="registered users" />
      </div>

      {/* ── Grille principale ── */}
      <div className="admin-dashboard-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 20 }}>

        {/* Commandes récentes */}
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div className="flex-between" style={{ padding: '20px 20px 0', flexWrap: 'wrap', gap: 8 }}>
            <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 700 }}>{t('admin', 'recent_orders')}</h3>
            <Link to="/admin/orders" style={{ fontSize: 13, color: 'var(--accent)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
              {t('common', 'view_all')} <ArrowUpRight size={14} />
            </Link>
          </div>
          {/* Table scrollable horizontalement */}
          <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <table className="table" style={{ minWidth: 480, marginTop: 12 }}>
              <thead>
                <tr>
                  <th>Order</th>
                  <th>Customer</th>
                  <th>{t('admin', 'status')}</th>
                  <th>Total</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {recentOrders.map(o => (
                  <tr key={o._id}>
                    <td style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 13 }}>#{o.orderNumber?.slice(-8)}</td>
                    <td>
                      <div style={{ fontSize: 14, fontWeight: 500 }}>{o.user?.name}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{o.user?.email}</div>
                    </td>
                    <td>
                      <span style={{ padding: '4px 10px', background: `${statusColor(o.status)}18`, color: statusColor(o.status), borderRadius: 8, fontSize: 12, fontWeight: 600, textTransform: 'capitalize' }}>{o.status}</span>
                    </td>
                    <td style={{ color: 'var(--gold)', fontWeight: 700 }}>${o.totalPrice?.toFixed(2)}</td>
                    <td style={{ color: 'var(--text-muted)', fontSize: 12 }}>{new Date(o.createdAt).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Low stock */}
        <div className="card">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
            <div style={{ width: 34, height: 34, background: 'rgba(245,158,11,0.12)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <AlertTriangle size={17} color="var(--warning)" />
            </div>
            <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 17, fontWeight: 700 }}>{t('admin', 'low_stock')}</h3>
          </div>
          {lowStock.length === 0 ? (
            <div style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '30px 0', fontSize: 14 }}>
              <span style={{ fontSize: 28, display: 'block', marginBottom: 8 }}>✅</span>
              All products well stocked
            </div>
          ) : lowStock.map(p => (
            <div key={p._id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 0', borderBottom: '1px solid var(--border)' }}>
              <img src={p.thumbnail} alt="" style={{ width: 40, height: 40, borderRadius: 10, objectFit: 'cover', background: 'var(--bg-secondary)', flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</div>
                <div style={{ fontSize: 12, marginTop: 2, color: p.stock === 0 ? 'var(--danger)' : 'var(--warning)', fontWeight: 600 }}>
                  {p.stock === 0 ? '✗ Out of stock' : `⚠ ${p.stock} left`}
                </div>
              </div>
              <Link to="/admin/products" className="btn btn-secondary btn-sm">Edit</Link>
            </div>
          ))}
        </div>
      </div>
    </AdminLayout>
  );
}